import { useEffect, useRef, useState } from "react";
import { api, ApiError, type ScriptListItem } from "../api/client";
import { readScriptFile } from "../scripts/files";

// Open dialog (spec §6.2): two tabs — "My scripts" (server storage) and "From
// this computer" (upload a .py/.txt). Opening yields {id, name, code}; a local
// file has id null (not yet saved to the server).

export type OpenResult = { id: number | null; name: string; code: string };

type Tab = "server" | "local";

export default function OpenDialog({
  onClose,
  onOpen,
}: {
  onClose: () => void;
  onOpen: (result: OpenResult) => void;
}) {
  const [tab, setTab] = useState<Tab>("server");
  const [scripts, setScripts] = useState<ScriptListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  function refresh() {
    setScripts(null);
    setError(null);
    api
      .listScripts()
      .then(setScripts)
      .catch((e) => setError(e instanceof ApiError ? e.message : "Could not load your scripts."));
  }

  useEffect(refresh, []);

  async function openServer(id: number) {
    setBusy(true);
    try {
      const full = await api.getScript(id);
      onOpen({ id: full.id, name: full.name, code: full.code });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not open that script.");
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: number, name: string) {
    if (!window.confirm(`Delete "${name}" from the server? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.deleteScript(id);
      setScripts((prev) => (prev ? prev.filter((s) => s.id !== id) : prev));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not delete that script.");
    } finally {
      setBusy(false);
    }
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const code = await readScriptFile(file);
      onOpen({ id: null, name: file.name, code });
    } catch {
      setError("Could not read that file.");
    }
  }

  const tabCls = (t: Tab) =>
    `px-4 py-2 text-sm ${
      tab === t ? "border-b-2 border-sky-400 text-sky-300" : "text-slate-400 hover:text-slate-200"
    }`;

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Open script"
    >
      <div
        className="flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-lg border border-slate-700 bg-slate-900 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-700 bg-slate-800 px-4 py-3">
          <h2 className="text-lg font-semibold text-slate-100">Open</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded bg-slate-700 px-3 py-1.5 text-sm font-medium text-slate-100 hover:bg-slate-600"
          >
            Close
          </button>
        </div>

        <div className="flex border-b border-slate-700">
          <button type="button" className={tabCls("server")} onClick={() => setTab("server")}>
            My scripts
          </button>
          <button type="button" className={tabCls("local")} onClick={() => setTab("local")}>
            From this computer
          </button>
        </div>

        {error && <div className="bg-red-950/50 px-4 py-2 text-sm text-red-300">{error}</div>}

        <div className="min-h-[12rem] overflow-auto p-4 text-sm">
          {tab === "server" ? (
            scripts === null ? (
              <p className="text-slate-400">Loading…</p>
            ) : scripts.length === 0 ? (
              <p className="text-slate-400">
                You have no saved scripts yet. Write one, then Save it — or open a <code>.py</code>{" "}
                from this computer.
              </p>
            ) : (
              <ul className="divide-y divide-slate-800">
                {scripts.map((s) => (
                  <li key={s.id} className="flex items-center gap-2 py-1.5">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => openServer(s.id)}
                      className="flex-1 truncate text-left font-mono text-emerald-300 hover:underline disabled:opacity-50"
                      title={s.name}
                    >
                      {s.name}
                    </button>
                    <span className="shrink-0 text-xs text-slate-500">
                      {new Date(s.updated_at).toLocaleString()}
                    </span>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => remove(s.id, s.name)}
                      className="shrink-0 rounded px-2 py-0.5 text-xs text-red-400 hover:bg-red-950/60 disabled:opacity-50"
                      title="Delete from server"
                    >
                      Delete
                    </button>
                  </li>
                ))}
              </ul>
            )
          ) : (
            <div className="flex flex-col items-start gap-3">
              <p className="text-slate-300">
                Choose a <code>.py</code> (or <code>.txt</code>) file. It opens exactly as written —
                nothing is added — so trainer-supplied scripts run unchanged.
              </p>
              <input
                ref={fileRef}
                type="file"
                accept=".py,.txt,text/x-python,text/plain"
                onChange={onFile}
                className="text-slate-300 file:mr-3 file:rounded file:border-0 file:bg-slate-700 file:px-3 file:py-1.5 file:text-slate-100 hover:file:bg-slate-600"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
